import { PropsWithChildren, ReactNode, useMemo } from 'react';

import { CircularProgress } from '@mui/material';

import { Alert } from '@app-velocity/ui';
import { I18nResources, useI18n } from '@app-velocity/i18n';

import { TAiReviewDto } from '@app-velocity/av-api-v2';

import { Restart } from './components/Restart';
import { Stop } from './components/Stop';
import { AIReviewStatusContext } from './context/AIReviewStatusContext';
import { Actions } from './components/Actions';

export type AIReviewStatusProps = PropsWithChildren<{
  review: TAiReviewDto;
  action?: ReactNode;
}>;

export const AIReviewStatus = ({
  children,
  action,
  review,
}: AIReviewStatusProps) => {
  const { t } = useI18n();
  const aiStatusText: string = useMemo(() => {
    if (!review) return t('prototypes:status.waiting');

    switch (review.finishStatus) {
      case 'ABORTED': {
        if (review.statusExplanation)
          return t('prototypes:status.aborted_with_explanation', {
            explanation: review.statusExplanation,
          });

        return t('prototypes:status.aborted');
      }

      case 'ABORTED_ERROR':
        return t('prototypes:status.aborted_error', {
          explanation: review.statusExplanation,
        });

      case 'ABORTED_BY_USER':
        return t('prototypes:status.aborted_by_user');
      case 'ABORTED_CYCLE_DETECTED':
        return t('prototypes:status.aborted_cycle_detected');
      case 'ABORTED_PAGE_CYCLE_DETECTED':
        return t('prototypes:status.aborted_page_cycle_detected');
      case 'ABORTED_TIMEOUT':
        return t('prototypes:status.aborted_timeout');
      case 'FINISHED_WITH_AI_AND_INTERACTION':
        return t('prototypes:status.finished_with_ai_and_interaction');
      case 'FINISHED_WITH_AI_AND_INDIRECT_INTERACTION':
        return t('prototypes:status.finished_with_ai_and_indirect_interaction');
      case 'FINISHED_WITH_AI':
        return t('prototypes:status.finished_with_ai');
      case 'FINISHED_WITH_INTERACTION':
        return t('prototypes:status.finished_with_interaction');
      case 'FINISHED_WITH_INDIRECT_INTERACTION':
        return t('prototypes:status.finished_with_indirect_interaction');
    }

    return t('prototypes:status.in_progress');
  }, [review, t]);

  const alertType = useMemo(() => {
    if (!review) return 'info';

    switch (review.reviewStatus) {
      case 'IN_PROGRESS':
        return 'info';

      case 'CANCELED': {
        if (
          review.finishStatus &&
          [
            'ABORTED',
            'ABORTED_CYCLE_DETECTED',
            'ABORTED_PAGE_CYCLE_DETECTED',
          ].includes(review.finishStatus)
        )
          return 'warning';

        return 'error';
      }

      case 'COMPLETED':
        return 'success';
      default:
        return 'info';
    }
  }, [review]);

  const alertTitle: I18nResources = useMemo(() => {
    if (!review) return 'prototypes:status.waiting';

    switch (review.reviewStatus) {
      case 'CANCELED':
        return 'prototypes:simulation_unsuccessful';
      case 'COMPLETED':
        return 'prototypes:simulation_completed';
      case 'IN_PROGRESS':
        return 'prototypes:simulation_in_progress';
      default:
        return 'prototypes:simulation_in_progress';
    }
  }, [review]);

  if (!review) return null;

  return (
    <AIReviewStatusContext.Provider value={{ review }}>
      <Alert
        severity={alertType}
        title={t(alertTitle)}
        message={aiStatusText}
        rightAction={action}
        customIcon={
          alertType === 'info' ? (
            <CircularProgress size={12} thickness={5} />
          ) : undefined
        }
      >
        {children}
      </Alert>
    </AIReviewStatusContext.Provider>
  );
};

AIReviewStatus.Restart = Restart;
AIReviewStatus.Stop = Stop;
AIReviewStatus.Actions = Actions;
